import { ArrowRight, CheckCircle2, Send, Sparkles } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import {
  CheckboxGroupField,
  FormSection,
  SelectField,
  TextareaField,
  TextField,
} from "./FormPageShared";
import { FormPortalHeader } from "./FormPortalHeader";

type FormErrors = Record<string, string>;
type InquiryPayload = Record<string, string | string[]>;

const sections = [
  { id: "contact-information", title: "Contact Information" },
  { id: "marketing-source", title: "How Did You Hear About Us?" },
  { id: "event-information", title: "Event Information" },
  { id: "service-interest", title: "Service Interest" },
  { id: "guest-experience", title: "Guest Experience Goals" },
  { id: "timeline-readiness", title: "Timeline and Readiness" },
  { id: "investment-comfort", title: "Investment Comfort" },
  { id: "referral-information", title: "Referral Information" },
  { id: "final-details", title: "Final Details" },
] as const;

const requiredFields: Record<string, string> = {
  fullName: "Full Name is required.",
  email: "Email Address is required.",
  phone: "Phone Number is required.",
  preferredContact: "Preferred Contact Method is required.",
  cityState: "City and State is required.",
  heardAbout: "Please tell us how you heard about Kreatethedate.",
  eventType: "Event Type is required.",
  eventTitle: "Event title is required.",
  celebratedName: "Celebrated or honored person is required.",
  eventDate: "Event Date is required.",
  dateConfirmed: "Please confirm the event date status.",
  guestCount: "Estimated Guest Count is required.",
  serviceInterest: "Please select at least one service interest.",
  eventTimeline: "Please select whether this is for an upcoming event, past event, or both.",
  goals: "Please share what you are hoping Kreatethedate can help you create, organize, or preserve.",
  guestExperience: "Please select at least one guest experience goal.",
  mattersMost: "Please select what matters most to you.",
  completionDate: "Desired completion date is required.",
  howSoon: "Please select how soon you hope to move forward.",
  readiness: "Please select your readiness.",
  investmentComfort: "Investment comfort is required.",
  preparedToMoveForward: "Please select your readiness to move forward.",
  isPartnerReferred: "Please tell us whether you were referred by a Kreatethedate partner.",
  confirmations: "Please confirm before submitting.",
};

const checkboxFields = new Set(["serviceInterest", "guestExperience", "mattersMost", "confirmations"]);

export function EventInquiryFormPage() {
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [discoveryEmailSent, setDiscoveryEmailSent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    document.title = "Kreatethedate Event Inquiry Form | Discovery Call Review";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Complete the Kreatethedate Event Inquiry Form so we can review your celebration, service interests, timeline, and next steps.",
      );
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const nextErrors = validateInquiry(formData);
    setErrors(nextErrors);

    const firstInvalidName = Object.keys(nextErrors)[0];
    if (firstInvalidName) {
      setSubmitError("Please complete all required fields before submitting your inquiry.");
      form.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)?.focus();
      return;
    }

    const payload = buildInquiryPayload(formData);

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/inquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error((await response.text()) || "Your inquiry could not be submitted.");
      const result = (await response.json()) as { discoveryEmail?: { sent?: boolean } };
      setDiscoveryEmailSent(Boolean(result.discoveryEmail?.sent));
      setSubmitted(true);
      formRef.current?.reset();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Your inquiry could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <a
        href="#event-inquiry-form"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#050505] focus:px-4 focus:py-3 focus:text-[#FF6A2B]"
      >
        Skip to Event Inquiry Form
      </a>
      <FormPortalHeader
        brandHref="https://www.kreatethedate.com"
        publicBackHref="https://www.kreatethedate.com"
        publicBackLabel="Kreatethedate"
      />

      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18">
        <img src="/assets/celebration-table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,.94),rgba(5,5,5,.74)_54%,rgba(5,5,5,.48)),linear-gradient(0deg,rgba(5,5,5,.42),rgba(5,5,5,.14))]" />
        <div className="relative mx-auto grid min-h-[48dvh] max-w-7xl items-end gap-8 px-4 pb-10 pt-24 text-[#fff9ef] sm:px-6 lg:grid-cols-[1fr_.72fr] lg:px-8 lg:pb-14">
          <div>
            <p className="inline-flex w-fit items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-[#FF6A2B]">
              <Sparkles className="size-4" aria-hidden="true" />
              For Discovery Call Review
            </p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              Event Inquiry Form
            </h1>
          </div>
          <div className="max-w-xl border-l-2 border-[#FF6A2B] pl-4 text-sm font-semibold leading-7 text-[#fff9ef]/76 lg:ml-auto">
            <p>
              Share your celebration details, service interests, timeline, and goals so Kreatethedate can review your
              inquiry and send the next step.
            </p>
            <p className="mt-3 text-[#FF6A2B]">This form connects directly to the Kreatethedate Lead Intake Engine.</p>
          </div>
        </div>
      </section>

      {submitted ? (
        <InquirySuccess discoveryEmailSent={discoveryEmailSent} />
      ) : (
        <section className="px-4 pb-28 pt-10 sm:px-6 lg:px-8 lg:pb-28 lg:pt-14">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[290px_1fr]">
            <aside className="hidden h-fit rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] lg:sticky lg:top-24 lg:block">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Inquiry sections</p>
              <ol className="mt-4 grid gap-2">
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="focus-ring group grid grid-cols-[34px_1fr] items-center gap-3 rounded-lg px-2 py-2 text-sm font-bold text-[#5c5246] transition hover:bg-[#fff9ef] hover:text-[#050505]"
                    >
                      <span className="grid size-8 place-items-center rounded-full border border-[#FF6A2B]/45 bg-[#050505] text-xs text-[#FF6A2B]">
                        {index + 1}
                      </span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <form id="event-inquiry-form" ref={formRef} noValidate onSubmit={handleSubmit} className="grid gap-6">
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Please complete this inquiry form so we can learn more about your celebration, event needs, timeline, and
                which Kreatethedate service may be the best fit. Required fields are marked with{" "}
                <span className="font-black text-[#D94A16]">*</span>.
              </p>
              {submitError ? (
                <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800" role="alert">
                  {submitError}
                </p>
              ) : null}

              <FormSection id="contact-information" number="01" title="Contact Information" formName="Event Inquiry Form">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="fullName" label="Full Name" required error={errors.fullName} autoComplete="name" />
                  <TextField name="email" label="Email Address" type="email" required error={errors.email} autoComplete="email" />
                  <TextField name="phone" label="Phone Number" type="tel" required error={errors.phone} autoComplete="tel" />
                  <SelectField
                    name="preferredContact"
                    label="Preferred Contact Method"
                    required
                    error={errors.preferredContact}
                    options={["Email", "Phone Call", "Text Message", "No preference"]}
                  />
                  <TextField name="cityState" label="City and State" required error={errors.cityState} className="md:col-span-2" />
                </div>
              </FormSection>

              <FormSection id="marketing-source" number="02" title="How Did You Hear About Us?" formName="Event Inquiry Form">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField
                    name="heardAbout"
                    label="How did you hear about Kreatethedate?"
                    required
                    error={errors.heardAbout}
                    className="md:col-span-2"
                    options={[
                      "Google Search",
                      "Instagram",
                      "Facebook",
                      "TikTok",
                      "Pinterest",
                      "YouTube",
                      "Referral from a friend or family member",
                      "Referral from a past Kreatethedate client",
                      "I viewed or attended a Kreatethedate event page",
                      "Wedding planner or event planner referral",
                      "Vendor or business referral",
                      "Flyer or business card",
                      "Website link shared with me",
                      "Returning client",
                      "Other",
                    ]}
                  />
                  <TextField
                    name="referredBy"
                    label="If someone referred you, please share their name"
                    className="md:col-span-2"
                  />
                </div>
              </FormSection>

              <FormSection id="event-information" number="03" title="Event Information" formName="Event Inquiry Form">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField
                    name="eventType"
                    label="What type of event or celebration are you planning?"
                    required
                    error={errors.eventType}
                    className="md:col-span-2"
                    options={[
                      "Birthday Celebration",
                      "Wedding",
                      "Engagement",
                      "Bridal Shower",
                      "Baby Shower",
                      "Graduation",
                      "Family Reunion",
                      "Anniversary",
                      "Memorial or Celebration of Life",
                      "Retirement Celebration",
                      "Church or Ministry Event",
                      "School Event",
                      "Sports Team Event",
                      "Business or Corporate Event",
                      "Holiday Celebration",
                      "Private Dinner or Gathering",
                      "Community Event",
                      "Other",
                    ]}
                  />
                  <TextField
                    name="eventTitle"
                    label="What is the name or title of your event?"
                    required
                    error={errors.eventTitle}
                    helperText={"Example: Jayden's Graduation Celebration or The Johnson Family Reunion."}
                    className="md:col-span-2"
                  />
                  <TextField
                    name="celebratedName"
                    label="Who is being celebrated or honored?"
                    required
                    error={errors.celebratedName}
                    className="md:col-span-2"
                  />
                  <TextField name="eventDate" label="What is your event date?" type="date" required error={errors.eventDate} />
                  <SelectField
                    name="dateConfirmed"
                    label="Is your event date confirmed?"
                    required
                    error={errors.dateConfirmed}
                    options={[
                      "Yes, the date is confirmed",
                      "No, the date may change",
                      "I am still choosing between dates",
                      "This is for a past event",
                    ]}
                  />
                  <TextField
                    name="eventLocation"
                    label="Where will the event take place?"
                    helperText="Venue name, city/state, or not confirmed yet."
                    className="md:col-span-2"
                  />
                  <SelectField
                    name="guestCount"
                    label="Estimated Guest Count"
                    required
                    error={errors.guestCount}
                    className="md:col-span-2"
                    options={["Under 25 guests", "25-50 guests", "51-100 guests", "101-150 guests", "151-250 guests", "250+ guests", "Not sure yet"]}
                  />
                </div>
              </FormSection>

              <FormSection id="service-interest" number="04" title="Service Interest" formName="Event Inquiry Form">
                <div className="grid gap-4">
                  <CheckboxGroupField
                    name="serviceInterest"
                    label="Which Kreatethedate service are you interested in?"
                    required
                    error={errors.serviceInterest}
                    options={[
                      "RSVP Hub",
                      "Memory Hub",
                      "Archive Hub",
                      "Signature Song",
                      "Signature Highlight Reel",
                      "RSVP Hub + Memory Hub Bundle",
                      "Legacy Experience Package",
                      "I am not sure and would like a recommendation",
                    ]}
                  />
                  <SelectField
                    name="eventTimeline"
                    label="Is this for an upcoming event, a past event, or both?"
                    required
                    error={errors.eventTimeline}
                    options={["Upcoming event", "Past event", "Both upcoming and past event needs", "I am not sure yet"]}
                  />
                  <TextareaField
                    name="goals"
                    label="What are you hoping Kreatethedate can help you create, organize, or preserve?"
                    required
                    error={errors.goals}
                    helperText="Share whether you need a digital RSVP page, a place to preserve memories, a private archive, a custom event song, a highlight reel, or help deciding what fits best."
                    rows={5}
                  />
                </div>
              </FormSection>

              <FormSection id="guest-experience" number="05" title="Guest Experience Goals" formName="Event Inquiry Form">
                <div className="grid gap-5">
                  <CheckboxGroupField
                    name="guestExperience"
                    label="What would you like guests, family, or loved ones to be able to do?"
                    required
                    error={errors.guestExperience}
                    options={[
                      "View event details in one place",
                      "RSVP online",
                      "Select meal options",
                      "View registry, contribution, or donation links",
                      "See event updates or announcements",
                      "Upload photos or videos",
                      "Leave memories, messages, or well wishes",
                      "Revisit memories after the event",
                      "Access multiple Kreatethedate event pages in one Archive Hub",
                      "Experience a custom song or music element",
                      "Watch a highlight reel",
                      "I am not sure yet",
                    ]}
                  />
                  <CheckboxGroupField
                    name="mattersMost"
                    label="What matters most to you about this experience?"
                    required
                    error={errors.mattersMost}
                    options={[
                      "Making the event easier to manage",
                      "Giving guests one simple link",
                      "Creating a more polished event experience",
                      "Preserving memories after the event",
                      "Making the celebration feel more personal",
                      "Honoring someone special",
                      "Creating something family and guests can revisit",
                      "Organizing multiple events in one place",
                      "I need help deciding",
                    ]}
                  />
                </div>
              </FormSection>

              <FormSection id="timeline-readiness" number="06" title="Timeline and Readiness" formName="Event Inquiry Form">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="completionDate" label="When would you like your project completed?" type="date" required error={errors.completionDate} />
                  <SelectField
                    name="howSoon"
                    label="How soon are you hoping to move forward?"
                    required
                    error={errors.howSoon}
                    options={["Immediately", "Within 1 week", "Within 2 weeks", "Within 30 days", "I am planning ahead", "I am only gathering information right now"]}
                  />
                  <SelectField
                    name="readiness"
                    label="Do you already have your event details, photos, videos, wording, or inspiration ready?"
                    required
                    error={errors.readiness}
                    className="md:col-span-2"
                    options={[
                      "Yes, most of my information is ready",
                      "I have some information ready",
                      "I am still gathering everything",
                      "I need help figuring out what is needed",
                      "Not applicable yet",
                    ]}
                  />
                </div>
              </FormSection>

              <FormSection id="investment-comfort" number="07" title="Investment Comfort" formName="Event Inquiry Form">
                <div className="grid gap-4">
                  <SelectField
                    name="investmentComfort"
                    label="Which investment range feels closest to what you are considering?"
                    required
                    error={errors.investmentComfort}
                    options={[
                      "$299-$499",
                      "$500-$799",
                      "$800-$1,199",
                      "$1,200-$1,499",
                      "$1,500+",
                      "I am interested in a bundle or package",
                      "I am not sure yet and would like a recommendation",
                    ]}
                  />
                  <SelectField
                    name="preparedToMoveForward"
                    label="If the recommended service is a good fit, are you prepared to move forward after the Discovery Call?"
                    required
                    error={errors.preparedToMoveForward}
                    options={[
                      "Yes, I am ready to move forward",
                      "Maybe, depending on the recommendation",
                      "I need to speak with someone else first",
                      "Not yet, I am still comparing options",
                      "I am only looking for general information right now",
                    ]}
                  />
                </div>
              </FormSection>

              <FormSection id="referral-information" number="08" title="Referral Information" formName="Event Inquiry Form">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField
                    name="isPartnerReferred"
                    label="Were you referred by one of our Kreatethedate partners?"
                    required
                    error={errors.isPartnerReferred}
                    className="md:col-span-2"
                    options={["Yes", "No", "Not Sure"]}
                  />
                  <TextField name="partnerName" label="If yes, who referred you?" />
                  <TextField name="partnerReferralCode" label="Referral code, if provided" />
                  <TextField name="partnerBusinessName" label="Partner business or organization name, if known" className="md:col-span-2" />
                </div>
              </FormSection>

              <FormSection id="final-details" number="09" title="Final Details" formName="Event Inquiry Form">
                <div className="grid gap-5">
                  <TextareaField
                    name="questions"
                    label="What questions would you like answered if we move forward with a Discovery Call?"
                    rows={4}
                  />
                  <TextareaField
                    name="additionalInfo"
                    label="Is there anything else we should know before reviewing your inquiry?"
                    rows={4}
                  />
                  <CheckboxGroupField
                    name="confirmations"
                    label="Please confirm before submitting."
                    required
                    error={errors.confirmations}
                    options={[
                      "I understand this form helps Kreatethedate review my inquiry and prepare for a possible Discovery Call.",
                      "I understand submitting this form does not guarantee availability or reserve my project date.",
                      "I understand that if my request aligns with Kreatethedate services and availability, I will receive a Discovery Call booking link by email.",
                      "I understand pricing, timeline, and recommendations will be discussed after my inquiry is reviewed.",
                      "I agree to be contacted by Kreatethedate about my inquiry.",
                    ]}
                  />
                </div>
              </FormSection>

              <div className="flex flex-col gap-3 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  After submission, your inquiry will be sent directly to the Kreatethedate Lead Intake Engine.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-6 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : "Submit My Event Inquiry"}
                  <Send className="size-4" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function InquirySuccess({ discoveryEmailSent }: { discoveryEmailSent: boolean }) {
  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl rounded-lg border border-[#D94A16]/24 bg-white p-6 text-center shadow-[0_24px_70px_rgba(45,32,8,.12)] sm:p-8">
        <span className="mx-auto grid size-16 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
          <CheckCircle2 className="size-9" aria-hidden="true" />
        </span>
        <h2 className="mt-6 font-display text-4xl font-bold text-[#050505]">Inquiry Received</h2>
        <div className="mx-auto mt-4 grid max-w-3xl gap-4 text-base font-semibold leading-8 text-[#5c5246]">
          <p>Thank you for submitting your Kreatethedate inquiry.</p>
          <p>
            Your details have been received and a lead record has been created for Kreatethedate review.
            {discoveryEmailSent
              ? " Please check your email for your Discovery Call next step."
              : " Kreatethedate will follow up with your Discovery Call next step."}
          </p>
        </div>
        <a
          href="https://www.kreatethedate.com"
          className="focus-ring mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
        >
          Return to Kreatethedate Website
          <ArrowRight className="size-4" aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}

function validateInquiry(formData: FormData) {
  const nextErrors: FormErrors = {};

  Object.entries(requiredFields).forEach(([fieldName, message]) => {
    if (checkboxFields.has(fieldName)) {
      const hasSelection = formData
        .getAll(fieldName)
        .filter((entry): entry is string => typeof entry === "string")
        .some((value) => value.trim());
      if (!hasSelection) nextErrors[fieldName] = message;
      return;
    }

    const value = String(formData.get(fieldName) || "").trim();
    if (!value) nextErrors[fieldName] = message;
  });

  const email = String(formData.get("email") || "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  return nextErrors;
}

function buildInquiryPayload(formData: FormData): InquiryPayload {
  const payload: InquiryPayload = {
    source: "Website Inquiry",
  };

  formData.forEach((value, fieldName) => {
    if (typeof value !== "string") return;
    const textValue = value.trim();
    if (!textValue) return;

    if (checkboxFields.has(fieldName)) {
      const existing = payload[fieldName];
      payload[fieldName] = Array.isArray(existing) ? [...existing, textValue] : [textValue];
      return;
    }

    payload[fieldName] = textValue;
  });

  payload.budget = scalar(payload.investmentComfort);
  payload.timeline = [
    labelValue("Event timeline", payload.eventTimeline),
    labelValue("Completion date", payload.completionDate),
    labelValue("Move-forward timeline", payload.howSoon),
    labelValue("Readiness", payload.readiness),
  ]
    .filter(Boolean)
    .join(" | ");
  payload.referralSource = [
    labelValue("Referred by", payload.referredBy),
    labelValue("Partner name", payload.partnerName),
    labelValue("Partner referral code", payload.partnerReferralCode),
    labelValue("Partner business", payload.partnerBusinessName),
  ]
    .filter(Boolean)
    .join(" | ");
  payload.marketingSource = scalar(payload.heardAbout);
  payload.notes = [
    labelValue("Event title", payload.eventTitle),
    labelValue("Celebrated or honored person", payload.celebratedName),
    labelValue("Date confirmed", payload.dateConfirmed),
    labelValue("Event location", payload.eventLocation),
    labelValue("Estimated guest count", payload.guestCount),
    labelValue("Goals", payload.goals),
    labelValue("Guest experience goals", payload.guestExperience),
    labelValue("What matters most", payload.mattersMost),
    labelValue("Prepared to move forward", payload.preparedToMoveForward),
    labelValue("Discovery call questions", payload.questions),
    labelValue("Additional information", payload.additionalInfo),
  ]
    .filter(Boolean)
    .join(" | ");

  return payload;
}

function labelValue(label: string, value: string | string[] | undefined) {
  const text = scalar(value);
  return text ? `${label}: ${text}` : "";
}

function scalar(value: string | string[] | undefined) {
  return Array.isArray(value) ? value.join(", ") : value || "";
}
